import { CalendarEvent, EventMember } from '../types/calendar';
import { FamilyMember } from '../store/familyStore';

/**
 * Enriches calendar events with current family member data (colors, names, avatars)
 * This ensures events always display the latest member information even if it was updated
 * after the event was created.
 * 
 * @param events - Array of calendar events to enrich
 * @param familyMembers - Current family members from the family store
 * @returns Array of enriched events with updated member data
 */
export function enrichEventsWithFamilyData(
  events: CalendarEvent[],
  familyMembers: FamilyMember[]
): CalendarEvent[] {
  if (!familyMembers || familyMembers.length === 0) {
    return events;
  }

  // Create a map of memberId -> current family member data for quick lookup
  const memberMap = new Map<string, FamilyMember>();
  familyMembers.forEach(member => {
    memberMap.set(member.userId, member);
  });

  return events.map(event => {
    // Enrich members array with current family data
    const enrichedMembers: EventMember[] = event.members && event.members.length > 0
      ? event.members.map(member => {
          const currentFamilyMember = memberMap.get(member.memberId);
          if (currentFamilyMember) {
            // Update with current family member data
            return {
              memberId: member.memberId,
              memberName: currentFamilyMember.name,
              memberColor: currentFamilyMember.color,
              memberAvatar: currentFamilyMember.avatar,
            };
          }
          // If member not found in family, keep original data
          return member;
        })
      : (event.memberId ? [{
          memberId: event.memberId,
          memberName: event.memberName || '',
          memberColor: event.memberColor || '#3b82f6',
          memberAvatar: event.memberAvatar,
        }] : []);

    // Enrich legacy single-member fields if they exist
    let enrichedMemberId = event.memberId;
    let enrichedMemberName = event.memberName;
    let enrichedMemberColor = event.memberColor;
    let enrichedMemberAvatar = event.memberAvatar;

    if (event.memberId) {
      const currentFamilyMember = memberMap.get(event.memberId);
      if (currentFamilyMember) {
        enrichedMemberName = currentFamilyMember.name;
        enrichedMemberColor = currentFamilyMember.color;
        enrichedMemberAvatar = currentFamilyMember.avatar;
      }
    }

    // If we have enriched members, use the first one for legacy fields
    if (enrichedMembers.length > 0) {
      enrichedMemberId = enrichedMembers[0].memberId;
      enrichedMemberName = enrichedMembers[0].memberName;
      enrichedMemberColor = enrichedMembers[0].memberColor;
      enrichedMemberAvatar = enrichedMembers[0].memberAvatar;
    }

    const enrichedEvent = {
      ...event,
      // Legacy fields
      memberId: enrichedMemberId,
      memberName: enrichedMemberName,
      memberColor: enrichedMemberColor,
      memberAvatar: enrichedMemberAvatar,
      // New: Multiple members support
      members: enrichedMembers,
    };
    return enrichedEvent;
  });
}
